import {
  Stack, Heading, Divider, Avatar, Box, CloseButton, Drawer, DrawerContent, Flex, HStack, Icon,
  IconButton, Image, Input, Menu, MenuButton, MenuDivider, MenuItem, MenuList, Text, Tooltip,
  useColorMode, useColorModeValue, useDisclosure, VStack,
} from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';
import { GoSidebarCollapse, GoSidebarExpand } from "react-icons/go";
import { PiHouseBold, PiBriefcaseBold, PiUsersBold, PiUserBold, PiChartBar } from "react-icons/pi"; 
import { FaTasks } from "react-icons/fa";
import { GrUserWorker } from "react-icons/gr";
import { FiUserCheck, FiDollarSign, FiFileText, FiClipboard,FiMenu, FiHome,FiChevronDown } from "react-icons/fi";
import { BiSupport } from "react-icons/bi"; 
import { HiOutlineDocumentReport } from "react-icons/hi";
import { Link, useLocation } from 'react-router-dom';
import NotificationsMenu from '../../admin/pages/notification/NotificationsMenu';
import './scrollbar.css';
import AttendanceSystem from "../../admin/pages/attendance/components/BillerAttendanceModal";
import HelpContactModal from "./HelpContactModal";
import { MdOutlineTimer } from 'react-icons/md';

// Static assets
const LogoBlack = require("../../images/logo.png");
const LogoWhite = require("../../images/logo.png");

// User data from local storage
const currentUser = JSON.parse(localStorage.getItem("user")) || {};
const userId = currentUser.userId || 0;
const role = localStorage.getItem("role");

// Define all possible navigation items
const LINK_ITEMS = [
  { name: "Overview", icon: PiHouseBold, to: "/dashboard" }, 
  { name: "My Jobs", icon: PiBriefcaseBold, to: "/jobs" }, 
  { name: "Jobs", icon: FiClipboard, to: "/jobs" }, 
  { name: "My Billers", icon: PiUsersBold, to: "/billers" }, 
  { name: "Billers", icon: GrUserWorker, to: "/billers" }, 
  { name: "Reports", icon: FiFileText, to: "/reports" }, 
  { name: "Task Report", icon: HiOutlineDocumentReport, to: "/biller-reports" }, 
  { name: "Tasks", icon: FaTasks, to: "/tasks" }, 
  { name: "Task & Reports", icon: HiOutlineDocumentReport, to: "/tasks-report" }, 
  { name: "Clients", icon: PiUsersBold, to: "/clients" }, 
  { name: "Productivity", icon: PiChartBar, to: "/worklog" }, 
  { name: "Production Report", icon: FiFileText, to: "/production-report" }, 
  { name: "Assignment", icon: FiHome, to: "/worklog" }, 
  { name: "Invoices", icon: FiDollarSign, to: "/" }, 
  { name: "Support", icon: BiSupport, to: "/support" }, 
  { name: "Time Doctor", icon: MdOutlineTimer, to: "/admin/time-doctor" },
  { name: "Attendance", icon: FiUserCheck, to: "/attendance" }, 
  { name: "Profile", icon: PiUserBold, to: "/profile" }, 
];

// Role-based navigation item filters
const getFilteredLinkItems = (userRole) => {
  let items = [...LINK_ITEMS];
  if (!userRole) {
    return items.filter(item => item.name !== "Attendance");
  }

  const roleFilters = {
    BILLER: ["Overview", "Clients", "Jobs", "Task Report", "Productivity", "Profile", "Tasks", "Production Report"],
    CLIENT: ["Overview", "Billers", "Jobs", "Productivity","Production Report", "Profile", "Support", "Attendance", "Task & Reports"],
    ADMIN: ["Overview", "Billers", "Clients", "Jobs","Productivity", "Production Report", "Profile", "Support","Time Doctor", "Attendance", "Task & Reports"],
  };

  items = items.filter(item => roleFilters[userRole]?.includes(item.name) || !roleFilters[userRole]);

  // Customize names for CLIENT role
  if (userRole === "CLIENT") {
    items = items.map(item => ({
      ...item,
      name: item.name === "Billers" ? "My Billers" : item.name === "Clients" ? "My Clients" : item.name,
    }));
  }

  return items;
};

const LinkItems = getFilteredLinkItems(role);

// Logout handler
const handleLogout = () => {
  localStorage.clear();
  window.location.href = '/';
};

// Sidebar content component
const SidebarContent = ({ sideBarWidth, handleSidebarWidth, onClose, ...rest }) => {
  const { colorMode } = useColorMode();
  const logo = colorMode === "light" ? LogoBlack : LogoWhite;
  const [searchTerm, setSearchTerm] = useState('');

  // Memoize filtered navigation items based on search term
  const filteredLinkItems = useMemo(() => {
    if (!searchTerm.trim()) return LinkItems;
    return LinkItems.filter(item => item.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [searchTerm]);

  return (
    <Box
      transition=".3s ease-in-out"
      bg={useColorModeValue('linear-gradient(to-r, #3A4B5C, #4A6574)', 'linear-gradient(to-r, #283E4A, #34495E)')}
      borderRight="1px"
      borderRightColor={useColorModeValue('gray.200', 'gray.700')}
      w={sideBarWidth === "small" ? "60px" : { base: 'full', lg: 60 }}
      pos="fixed"
      h="full"
      overflowY="scroll"
      css={{ '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none', '-ms-overflow-style': 'none' }}
      overflowX="hidden"
      {...rest}
    >
      {/* Header with logo and toggle */}
      <Flex h="20" alignItems="center" mx={2} justifyContent="space-between">
        <IconButton
          icon={sideBarWidth === "small" ? <GoSidebarCollapse /> : <GoSidebarExpand />}
          variant="ghost"
          colorScheme="green"
          display={{ base: 'none', lg: 'flex' }}
          onClick={handleSidebarWidth}
        />
        <Link to="/">
          <Image
            src={logo}
            w={150}
            loading="lazy"
            display={sideBarWidth === "small" ? "none" : "block"}
            transition="margin 0.3s ease-in-out"
            cursor="pointer"
          />
        </Link>
        <CloseButton display={{ base: 'flex', lg: 'none' }} onClick={onClose} />
      </Flex>

      {/* User profile */}
      <Box>
        <Divider display={sideBarWidth === "small" ? "none" : "block"} />
        <Box mb={5} p={2}>
          <Flex align="center">
            <Link to="/profile">
              <Avatar size={sideBarWidth === "small" ? "md" : "lg"} src={currentUser.profilePicture} cursor="pointer" />
            </Link>
            {sideBarWidth !== "small" && (
              <Stack ml={1.5} spacing={1}>
                <Heading fontSize="lg">{currentUser.userName}</Heading>
                <Text color="gray" fontSize="sm">{currentUser.email}</Text>
              </Stack>
            )}
          </Flex>
        </Box>
      </Box>

      {/* Search input */}
      <Input
        placeholder="Search"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        mb={3}
        display={sideBarWidth === "small" ? "none" : "block"}
        borderRadius="md"
        borderColor={useColorModeValue('gray.300', 'gray.600')}
        _focus={{ borderColor: useColorModeValue('teal.500', 'teal.300'), boxShadow: '0 0 0 1px #38B2AC' }}
        _placeholder={{ color: useColorModeValue('gray.500', 'gray.400') }}
        p={2}
        fontSize="sm"
        bg={useColorModeValue('white', 'gray.800')}
        transition="all 0.2s ease-in-out"
        width="80%"
        maxWidth="500px"
        ml={4}
      />

      {/* Navigation items */}
      {filteredLinkItems.map((link) => (
        <Link to={link.to} key={link.name}>
          <NavItem to={link.to} sideBarWidth={sideBarWidth} icon={link.icon}>{link.name}</NavItem>
        </Link>
      ))}
    </Box>
  );
};

// Navigation item component
const NavItem = ({ icon, children, to, sideBarWidth, ...rest }) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Box as="a" href="#" style={{ textDecoration: 'none' }} _focus={{ boxShadow: 'none' }}>
      <Flex
        align="center"
        p="4"
        ml={2}
        borderRadius="lg"
        role="group"
        cursor="pointer"
        bg={isActive ? "#6de90f" : "none"}
        color={isActive ? "white" : "inherit"}
        _hover={{ bg: '#6de90f', color: 'white' }}
        {...rest}
      >
        {icon && (
          <Tooltip
            hasArrow
            label={children}
            placement="right"
            bg="green"
            display={sideBarWidth === "small" ? "flex" : "none"}
          >
            <Icon mr="4" fontSize="16" as={icon} />
          </Tooltip>
        )}
        <Box display={sideBarWidth === "small" ? "none" : "block"}>{children}</Box>
      </Flex>
    </Box>
  );
};

// Mobile navigation component
const MobileNav = ({ onOpen, sideBarWidth, handleColorModeToggle, ...rest }) => {
  const { colorMode } = useColorMode();
  const logo = colorMode === "light" ? LogoBlack : LogoWhite;
  const { isOpen: isHelpOpen, onOpen: onHelpOpen, onClose: onHelpClose } = useDisclosure();

  return (
    <Flex
      px={{ base: 4, md: 4 }}
      height="20"
      alignItems="center"
      bg={useColorModeValue('#FAFAF7', 'gray.900')}
      borderBottomWidth="1px"
      borderBottomColor={useColorModeValue('gray.200', 'gray.700')}
      justifyContent={{ base: 'space-between', lg: 'space-between' }}
      {...rest}
    >
      <IconButton
        display={{ base: 'flex', lg: 'none' }}
        onClick={onOpen}
        variant="outline"
        aria-label="open menu"
        icon={<FiMenu />}
        bgColor="#FAFAF7"
      />
      <Flex>
        <Link to="/">
          <Image
            src={logo}
            w={150}
            loading="lazy"
            ml={sideBarWidth === "large" ? "250px" : "70px"}
            display={{ base: "none", lg: sideBarWidth === "small" ? "flex" : "none" }}
            transition="margin 0.3s ease-in-out"
            cursor="pointer"
          />
        </Link>
      </Flex>
      <HStack spacing={{ base: '0', md: '6' }}>
        <NotificationsMenu />
        {role === 'BILLER' && <AttendanceSystem billerId={userId} />}
        <Flex alignItems="center">
          <Menu>
            <MenuButton py={2} transition="all 0.3s" _focus={{ boxShadow: 'none' }}>
              <HStack>
                <Avatar
                  size="sm"
                  src={currentUser.profilePicture}
                  name={currentUser.userName || ""}
                  bg={useColorModeValue("gray.200", "gray.600")}
                  color={useColorModeValue("black", "white")}
                />
                <VStack display={{ base: 'none', md: 'flex' }} alignItems="flex-start" spacing="1px" ml="2">
                  <Text fontSize="sm" fontWeight="bold">{currentUser.fname}</Text>
                  <Text fontSize="sm" color="gray.600">{currentUser.userName}</Text>
                </VStack>
                <Box display={{ base: 'none', md: 'flex' }}><FiChevronDown /></Box>
              </HStack>
            </MenuButton>
            <MenuList bg={useColorModeValue('white', 'gray.900')} borderColor={useColorModeValue('gray.200', 'gray.700')}>
              <MenuItem><Link to="/profile">Profile</Link></MenuItem>
              {role === "CLIENT" && <MenuItem onClick={onHelpOpen}>Help/Contact</MenuItem>}
              <MenuDivider />
              <MenuItem onClick={handleLogout}>Sign out</MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </HStack>
      <HelpContactModal isOpen={isHelpOpen} onClose={onHelpClose} />
    </Flex>
  );
};

// Main SideBar component
const SideBar = ({ sideBarWidth, handleSidebarWidth }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { toggleColorMode } = useColorMode();

  const handleColorModeToggle = () => toggleColorMode();

  return (
    <Box>
      <SidebarContent
        sideBarWidth={sideBarWidth}
        handleSidebarWidth={handleSidebarWidth}
        onClose={onClose}
        display={{ base: 'none', md: 'none', lg: "block" }}
      />
      <Drawer
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
        returnFocusOnClose={false}
        onOverlayClick={onClose}
        size="sm"
      >
        <DrawerContent>
          <SidebarContent sideBarWidth="large" onClose={onClose} onClick={onClose} />
        </DrawerContent>
      </Drawer>
      <MobileNav
        sideBarWidth={sideBarWidth}
        onOpen={onOpen}
        handleColorModeToggle={handleColorModeToggle}
        onClose={onClose}
      />
    </Box>
  );
};

export default SideBar;